<div class="board">
  <a class="back" routerLink="/admin/spaces/events">← Spaces</a>

  <div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>
  <div class="notice bad" *ngIf="error">{{ error }}</div>

  <ng-container *ngIf="!loading && event as e">
    <div class="head">
      <div>
        <!-- The title is the way in to everything this space is set to. It is
             the biggest word on the page, so it is where a reader already
             points when they want to know more about the space itself. -->
        <h2>
          <button type="button" class="title-button" (click)="openPreferences()">
            {{ e.title }}
          </button>
        </h2>
        <p class="sub title-hint">Click the name for all of this space's settings and a QR code to join</p>
        <div class="pills">
          <span class="pill tag">{{ e.tag }}</span>
          <span class="pill tag">{{ e.type }}</span>
          <span class="pill tag" *ngIf="e.isPrivate">Private</span>
          <span class="pill tag" *ngIf="e.lobbyMode">Lobby: {{ e.lobbyMode }}</span>
          <span class="pill tag" *ngIf="e.claimedAt">Claimed {{ e.claimedAt | date: 'd MMM yyyy' }}</span>
          <span class="pill" [attr.data-state]="e.lobby.state" [title]="stateHint[e.lobby.state]">
            {{ stateLabel[e.lobby.state] }}
          </span>
        </div>
        <p class="sub">
          Created {{ e.createdAt | date: 'd MMM yyyy' }}
          <span *ngIf="e.startDate"> · runs {{ e.startDate | date: 'd MMM yyyy' }}<span *ngIf="e.endDate && e.endDate !== e.startDate"> – {{ e.endDate | date: 'd MMM yyyy' }}</span></span>
          <span *ngIf="e.registrationStart || e.registrationEnd">
            · registration {{ e.registrationStart ? (e.registrationStart | date: 'd MMM') : '…' }} – {{ e.registrationEnd ? (e.registrationEnd | date: 'd MMM yyyy') : '…' }}
          </span>
          <span *ngIf="e.participantsLimit"> · limit {{ e.participantsLimit }}</span>
        </p>
        <p class="sub" *ngIf="location">{{ location }}</p>
        <p class="sub" *ngIf="e.websiteUrl">
          <a [href]="e.websiteUrl" target="_blank" rel="noopener">{{ e.websiteUrl }}</a>
        </p>
        <p class="sub mono">{{ e.id }}</p>
      </div>
      <div class="head-actions">
        <app-range-switch [days]="days" (daysChange)="setRange($event)"></app-range-switch>
        <button mat-stroked-button (click)="load()" [disabled]="loading">Refresh</button>
      </div>
    </div>

    <div class="notice info">{{ advice }}</div>

    <div class="stats">
      <div class="stat">
        <div class="n">{{ e.memberCount }}</div>
        <div class="l">Members</div>
        <div class="hint">+{{ e.joinedSince }} in range · {{ silentMembers }} never posted</div>
      </div>
      <div class="stat">
        <div class="n">{{ e.lobby.window.messages }}<small>/ {{ e.lobby.allTime.messages }}</small></div>
        <div class="l">Lobby messages</div>
        <div class="hint">in range / all time · {{ e.lobby.allTime.reactions }} reactions</div>
      </div>
      <div class="stat">
        <div class="n">{{ e.lobby.window.voices }}<small>/ {{ e.lobby.allTime.voices }}</small></div>
        <div class="l">Voices<app-info-hint label="Voices"
          text="People who posted at least one message: in range, then ever."
        ></app-info-hint></div>
        <div class="hint">{{ percent(e.participationRate) }} of members ever posted</div>
      </div>
      <div class="stat" [class.warn]="(organizerShare() ?? 0) >= 80 && e.lobby.allTime.messages > 3">
        <div class="n">{{ organizerShare() === null ? '—' : organizerShare() + '%' }}</div>
        <div class="l">Organizer share<app-info-hint label="Organizer share"
          text="Share of lobby messages from the owner or a moderator, in range (all time when the range is empty)."
        ></app-info-hint></div>
        <div class="hint">{{ e.lobby.window.turns }} turns in range<app-info-hint label="Turns"
          text="How many times the speaker changed hands. 4 is where the room counts as a conversation."
        ></app-info-hint></div>
      </div>
      <div class="stat" [class.good]="e.peerChats.total > 0">
        <div class="n">{{ e.peerChats.total }}</div>
        <div class="l">Member-to-member chats<app-info-hint label="Member-to-member chats"
          text="One-to-one chats between two members of this space, all time. The connections the lobby is meant to produce."
        ></app-info-hint></div>
        <div class="hint">{{ e.peerChats.replied }} replied · {{ e.peerChats.inWindow }} active in range</div>
        <button
          *ngIf="e.peerChats.total"
          type="button"
          class="drill"
          (click)="openPeerChats()"
        >
          Who chatted with whom
        </button>
      </div>
      <div class="stat" [class.warn]="e.pendingRequests > 0">
        <div class="n">{{ e.pendingRequests }}</div>
        <div class="l">Waiting to join</div>
        <div class="hint">{{ e.rejectedRequests }} rejected</div>
      </div>
      <div class="stat" [class.warn]="hiddenPosts > 0">
        <div class="n">{{ e.posts }}</div>
        <div class="l">Feed posts<app-info-hint label="Feed posts"
          text="Posts on the main Updates feed that tag this space. Not the lobby — a post here never appears in the room, and the lobby's messages are never counted here."
        ></app-info-hint></div>
        <div class="hint">
          <ng-container *ngIf="hiddenPosts">{{ hiddenPosts }} no member can see</ng-container>
          <ng-container *ngIf="!hiddenPosts">{{ e.posts ? 'live on the feed' : 'nobody has posted about it' }}</ng-container>
        </div>
      </div>
    </div>

    <app-daily-bars *ngIf="e.daily.length" title="Lobby messages per day" [bars]="dailyBars">
      last {{ e.range.days }} days ·
      {{ e.lobby.window.lastMessageAt ? 'last message ' + ago(e.lobby.window.lastMessageAt) : 'no messages in range' }}
    </app-daily-bars>

    <section class="panel posts" *ngIf="e.feedPosts?.length">
      <h3>On the Updates feed ({{ e.feedPosts.length }})</h3>
      <p class="meta">
        Posts elsewhere in the app that tag this space. They are not lobby messages — members
        see these in Updates, not in the room.
      </p>
      <app-feed-post-list
        [posts]="e.feedPosts"
        [showAuthor]="true"
        [hiddenIds]="hiddenPostIds"
        [notes]="postNotes"
      ></app-feed-post-list>
    </section>

    <div class="grid">
      <section class="panel">
        <h3>Organizer</h3>
        <ng-container *ngIf="e.owner; else unclaimed">
          <app-person-link [big]="true" [uid]="e.owner.uid" [name]="e.owner.name" (failed)="error = $event"></app-person-link>
          <div class="meta">
            {{ [e.owner.role, e.owner.companyName].join(' · ') }}
            <span *ngIf="e.owner.email"> · {{ e.owner.email }}</span>
          </div>
          <div class="meta">last seen {{ ago(e.owner.lastSeen) }}</div>
        </ng-container>
        <ng-template #unclaimed>
          <p class="meta">
            Nobody owns this space.
            <span *ngIf="e.spaceClaimContactName || e.spaceClaimContactEmail">
              The claim invitation went to {{ e.spaceClaimContactName || '' }}
              {{ e.spaceClaimContactEmail ? '(' + e.spaceClaimContactEmail + ')' : '' }}.
            </span>
          </p>
        </ng-template>

        <h3 *ngIf="e.moderators.length">Moderators</h3>
        <ul class="people" *ngIf="e.moderators.length">
          <li *ngFor="let m of e.moderators">
            <app-person-link [uid]="m.uid" [name]="m.name" (failed)="error = $event"></app-person-link>
            <span class="meta">{{ m.companyName || m.email || '' }} · last seen {{ ago(m.lastSeen) }}</span>
          </li>
        </ul>
      </section>

      <section class="panel" *ngIf="e.description">
        <h3>Description</h3>
        <blockquote>{{ e.description }}</blockquote>
      </section>
    </div>

    <section class="panel" *ngIf="pending.length">
      <h3>Waiting to join ({{ pending.length }})</h3>
      <table class="inner">
        <thead>
          <tr><th>Who</th><th>Requested</th><th>Waiting</th></tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of pending">
            <td>
              <app-person-link [uid]="r.uid" [name]="r.name" (failed)="error = $event"></app-person-link>
              <div class="meta">{{ [r.role, r.companyName].join(' · ') }}</div>
            </td>
            <td class="meta">{{ r.requestedAt | date: 'd MMM yyyy, HH:mm' }}</td>
            <td class="meta">{{ ago(r.requestedAt) }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="filters">
      <h3 class="inline">Members ({{ e.memberCount }})</h3>
      <span class="spacer"></span>
      <span class="label">Sort</span>
      <button [class.on]="memberSort === 'talkative'" (click)="memberSort = 'talkative'">Most talkative</button>
      <button [class.on]="memberSort === 'joined'" (click)="memberSort = 'joined'">Recently joined</button>
      <button [class.on]="memberSort === 'seen'" (click)="memberSort = 'seen'">Recently seen</button>
    </div>

    <table *ngIf="members.length">
      <thead>
        <tr>
          <th>Member</th>
          <th>Joined</th>
          <th>Last seen in app</th>
          <th class="num">Lobby messages<app-info-hint label="Lobby messages"
            text="Messages this person posted in this lobby: in range, then all time."
          ></app-info-hint></th>
          <th>Last posted</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let m of members" [class.silent]="!m.lobby.messages">
          <td>
            <app-person-link [uid]="m.uid" [name]="m.name" (failed)="error = $event"></app-person-link>
            <span class="pill owner" *ngIf="m.isOwner">Owner</span>
            <span class="pill mod" *ngIf="m.isModerator && !m.isOwner">Moderator</span>
            <div class="meta">{{ [m.role, m.companyName].join(' · ') }}<span *ngIf="m.email"> · {{ m.email }}</span></div>
          </td>
          <td class="meta">{{ m.joinedAt | date: 'd MMM yyyy' }}</td>
          <td class="meta">{{ ago(m.lastSeen) }}</td>
          <td class="num">
            {{ m.lobby.inWindow }}
            <span class="meta">{{ m.lobby.messages }} all time</span>
          </td>
          <td class="meta">{{ ago(m.lobby.lastAt) }}</td>
        </tr>
      </tbody>
    </table>
    <p class="empty" *ngIf="!members.length">Nobody has joined this space.</p>

    <section class="panel" *ngIf="e.formerVoices.length">
      <h3>Posted, no longer members ({{ e.formerVoices.length }})</h3>
      <ul class="people">
        <li *ngFor="let v of e.formerVoices">
          <app-person-link [uid]="v.uid" [name]="v.uid" (failed)="error = $event"></app-person-link>
          <span class="meta">{{ v.messages }} messages · last {{ ago(v.lastAt) }}</span>
        </li>
      </ul>
    </section>

    <section class="panel" *ngIf="rejected.length">
      <h3>Rejected ({{ rejected.length }})</h3>
      <ul class="people">
        <li *ngFor="let r of rejected">
          <span>
            <app-person-link [uid]="r.uid" [name]="r.name" (failed)="error = $event"></app-person-link>
            <span class="pill rejected">{{ r.status }}</span>
          </span>
          <span class="meta">
            {{ r.lastModified | date: 'd MMM yyyy' }}{{ r.rejectionComment ? ' · ' + r.rejectionComment : '' }}
          </span>
        </li>
      </ul>
    </section>
  </ng-container>
</div>
